import { Globe, BookOpenCheck, Languages } from "lucide-react";
import { useTranslation } from "@/i18n";
import LanguagePicker from "@/LanguagePicker";
import SocialFollowRow from "@/SocialFollowRow";

export default function AboutPage() {
  const [t] = useTranslation();

  return (
    <div className="min-h-screen" data-testid="about-page">
      <header className="px-6 md:px-10 py-6 md:py-8 border-b border-white/5">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="home-link">
            <img src="/brand-s.png" alt="Storylo" className="w-9 h-9 rounded-full" data-testid="brand-logo" />
            <div>
              <div className="font-display text-lg leading-none">Storylo</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--muted)] mt-1">
                {t("tagline")}
              </div>
            </div>
          </a>
          <nav className="flex items-center gap-3 sm:gap-4 text-sm">
            <a href="/" className="text-[color:var(--muted)] hover:text-white transition-colors">{t("nav_all")}</a>
            <a href="/impact" className="text-[color:var(--muted)] hover:text-white transition-colors">{t("nav_impact")}</a>
            <a href="/about" className="text-white" data-testid="nav-about">{t("nav_about")}</a>
            <LanguagePicker />
          </nav>
        </div>
      </header>

      <main className="px-6 md:px-10 py-12 md:py-20">
        <div className="max-w-[840px] mx-auto">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[color:var(--accent)] mb-4">
            {t("mission_eyebrow")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {t("about_h1_a")}
            <em className="block text-[color:var(--accent)] not-italic mt-2">{t("about_h1_b")}</em>
          </h1>

          <p
            className="mt-8 text-lg sm:text-xl leading-relaxed text-white/85"
            data-testid="mission-statement"
          >
            {t("about_mission")}
          </p>

          <div className="grid sm:grid-cols-3 gap-4 mt-12">
            <div className="panel p-5" data-testid="about-pillar-honesty">
              <BookOpenCheck className="w-5 h-5 text-[color:var(--accent)] mb-3" />
              <div className="font-semibold text-sm mb-1.5">{t("about_p1_t")}</div>
              <p className="text-xs text-[color:var(--muted)] leading-relaxed">{t("about_p1_d")}</p>
            </div>
            <div className="panel p-5" data-testid="about-pillar-decide">
              <Globe className="w-5 h-5 text-[color:var(--accent)] mb-3" />
              <div className="font-semibold text-sm mb-1.5">{t("about_p2_t")}</div>
              <p className="text-xs text-[color:var(--muted)] leading-relaxed">{t("about_p2_d")}</p>
            </div>
            <div className="panel p-5" data-testid="about-pillar-languages">
              <Languages className="w-5 h-5 text-[color:var(--accent)] mb-3" />
              <div className="font-semibold text-sm mb-1.5">{t("about_p3_t")}</div>
              <p className="text-xs text-[color:var(--muted)] leading-relaxed">{t("about_p3_d")}</p>
            </div>
          </div>

          <div className="mt-12">
            <SocialFollowRow />
          </div>

          <a
            href="/"
            className="inline-flex items-center gap-2 mt-10 text-sm text-[color:var(--accent)] hover:underline"
            data-testid="about-browse-stories"
          >
            {t("about_browse")}
          </a>
        </div>
      </main>

      <footer className="mt-16 border-t border-white/5 px-6 md:px-10 py-6">
        <div className="max-w-[1200px] mx-auto flex flex-wrap items-center justify-between gap-3 text-xs text-[color:var(--muted)]">
          <div>
            {t("footer_made_by")} <a href="https://hadfun.co.uk" className="text-white hover:text-[color:var(--accent)] transition-colors">Hadfun</a>
          </div>
          <a href="/studio" className="opacity-30 hover:opacity-100 transition-opacity">{t("nav_studio")}</a>
        </div>
      </footer>
    </div>
  );
}
